import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecordings } from '../../contexts/RecordingContext';
import { useAudioPlayer } from '../../contexts/AudioPlayerContext';
import { useVenueRecordings } from '../../hooks/useWebSocket';
import { recordings as recordingsApi } from '../../services/api';
import { motion } from 'framer-motion';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Music,
  Play,
  Download,
  Share2,
  MoreVertical,
  Clock,
  Eye,
  Trash2,
  Edit,
  Globe,
  Lock,
  Loader2,
  Search,
  Filter,
  Calendar,
  TrendingUp,
  Grid,
  List,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import ShareDialog from './ShareDialog';
import EditRecordingDialog from './EditRecordingDialog';
import NewRecordingDialog from './NewRecordingDialog';
import RecordingCard from './RecordingCard';
import FloatingRecordingWidget from './FloatingRecordingWidget';

const RecordingDashboard: React.FC = () => {
  const navigate = useNavigate();
  const {
    profile,
    recordings,
    fetchProfile,
    fetchRecordings,
    deleteRecording,
    isLoading,
    error,
  } = useRecordings();

  const { play, currentRecording, isPlaying } = useAudioPlayer();

  const [selectedRecording, setSelectedRecording] = useState<any>(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [newRecordingOpen, setNewRecordingOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterVenue, setFilterVenue] = useState('all');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [showFloatingWidget, setShowFloatingWidget] = useState(true);
  const [selectedRecordingIds, setSelectedRecordingIds] = useState<Set<string>>(new Set());
  
  // Get real-time recording updates for selected venue
  const { activeRecordings, splData } = useVenueRecordings(
    filterVenue !== 'all' ? filterVenue : profile?.venues?.[0]?.id
  );

  useEffect(() => {
    fetchProfile();
    fetchRecordings();
    
    // Set up periodic refresh every 30 seconds
    const interval = setInterval(() => {
      fetchRecordings();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [fetchProfile, fetchRecordings]);

  const handlePlay = async (recording: any) => {
    console.log('Playing recording:', recording);

    // Always fetch fresh details to get a new presigned URL
    if (recording.status === 'ready') {
      try {
        console.log('Fetching fresh recording details for:', recording.id);
        const response = await fetchRecordingDetails(recording.id);
        console.log('Fetched recording details:', response);
        // Start playback with fresh URL (no navigation)
        await play(response);
      } catch (err) {
        console.error('Failed to fetch recording details:', err);
        // Fallback to existing data if fetch fails
        await play(recording);
      }
    }
  };

  const handleNavigate = (recording: any) => {
    // Navigate to recording detail page
    navigate(`/recordings/${recording.id}`);
  };

  const fetchRecordingDetails = async (recordingId: string) => {
    const response = await recordingsApi.get(recordingId);
    return response.data;
  };

  const handleShare = (recording: any) => {
    setSelectedRecording(recording);
    setShareDialogOpen(true);
  };

  const handleEdit = (recording: any) => {
    setSelectedRecording(recording);
    setEditDialogOpen(true);
  };

  const handleDelete = async (recording: any) => {
    if (window.confirm(`Are you sure you want to delete "${recording.title}"?`)) {
      try {
        await deleteRecording(recording.id);
      } catch (err) {
        console.error('Failed to delete recording:', err);
      }
    }
  };

  // Selection handlers
  const toggleRecordingSelection = (recordingId: string) => {
    setSelectedRecordingIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(recordingId)) {
        newSet.delete(recordingId);
      } else {
        newSet.add(recordingId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedRecordingIds.size === filteredRecordings.length && filteredRecordings.length > 0) {
      // Deselect all
      setSelectedRecordingIds(new Set());
    } else {
      // Select all filtered recordings
      setSelectedRecordingIds(new Set(filteredRecordings.map(r => r.id)));
    }
  };

  const handleBatchDelete = async () => {
    const count = selectedRecordingIds.size;
    if (count === 0) return;

    if (window.confirm(`Are you sure you want to delete ${count} recording${count > 1 ? 's' : ''}?`)) {
      try {
        // Delete all selected recordings
        await Promise.all(
          Array.from(selectedRecordingIds).map(id => deleteRecording(id))
        );
        // Clear selection after successful delete
        setSelectedRecordingIds(new Set());
      } catch (err) {
        console.error('Failed to delete recordings:', err);
      }
    }
  };

  const filteredRecordings = Array.isArray(recordings) ? recordings.filter(recording => {
    const matchesSearch = recording.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         recording.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesVenue = filterVenue === 'all' || recording.venueId === filterVenue;
    return matchesSearch && matchesVenue;
  }) : [];

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'N/A';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  };

  if (isLoading && !recordings.length) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Recording Dashboard</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Welcome back, {profile?.artistName || 'Artist'}
          </p>
        </div>
        <Button 
          className="bg-purple-600 hover:bg-purple-700"
          onClick={() => setNewRecordingOpen(true)}
        >
          <Music className="mr-2 h-4 w-4" />
          New Recording
        </Button>
      </div>

      {/* Active Recordings Alert */}
      {activeRecordings.length > 0 && (
        <Card className="border-red-500 bg-red-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-red-700 flex items-center gap-2">
              <div className="w-3 h-3 bg-red-600 rounded-full animate-pulse" />
              Live Recording in Progress
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {activeRecordings.map((recording) => (
                <div key={recording.id} className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{recording.title || 'Untitled Recording'}</p>
                    <p className="text-sm text-gray-600">
                      Started {(() => {
                        const time = recording.startTime || recording['start_time'];
                        return time ? formatDistanceToNow(new Date(time), { addSuffix: true }) : 'Unknown time';
                      })()}
                    </p>
                  </div>
                  {recording.status === 'uploading' && (
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-sm">Uploading...</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Recordings
            </CardTitle>
            <Music className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{recordings.length}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Views
            </CardTitle>
            <Eye className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {recordings.reduce((sum, r) => sum + (r.viewCount || r.view_count || 0), 0)}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Public Recordings
            </CardTitle>
            <Globe className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {recordings.filter(r => r.isPublic).length}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Venues
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {profile?.venuesCount || 0}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recordings Table */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Your Recordings</CardTitle>
            <div className="flex gap-2">
              {/* View Mode Toggle */}
              <div className="flex border rounded-md overflow-hidden">
                <motion.button
                  onClick={() => setViewMode('grid')}
                  className={`px-3 py-2 flex items-center gap-1 ${
                    viewMode === 'grid'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Grid className="h-4 w-4" />
                  <span className="text-sm">Gallery</span>
                </motion.button>
                <motion.button
                  onClick={() => setViewMode('table')}
                  className={`px-3 py-2 flex items-center gap-1 ${
                    viewMode === 'table'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <List className="h-4 w-4" />
                  <span className="text-sm">Table</span>
                </motion.button>
              </div>

              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 h-4 w-4" />
                <Input
                  placeholder="Search recordings..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-64"
                />
              </div>
              {profile?.venues && profile.venues.length > 1 && (
                <select
                  value={filterVenue}
                  onChange={(e) => setFilterVenue(e.target.value)}
                  className="px-3 py-2 border rounded-md"
                >
                  <option value="all">All Venues</option>
                  {profile.venues.map(venue => (
                    <option key={venue.id} value={venue.id}>
                      {venue.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Gallery View */}
          {viewMode === 'grid' && (
            <motion.div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              {filteredRecordings.map((recording, index) => (
                <motion.div
                  key={recording.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <RecordingCard
                    recording={recording}
                    onPlay={handlePlay}
                    onNavigate={handleNavigate}
                    onEdit={(rec) => {
                      setSelectedRecording(rec);
                      setEditDialogOpen(true);
                    }}
                    onShare={(rec) => {
                      setSelectedRecording(rec);
                      setShareDialogOpen(true);
                    }}
                    onDelete={handleDelete}
                    isPlaying={currentRecording?.id === recording.id && isPlaying}
                  />
                </motion.div>
              ))}
              {filteredRecordings.length === 0 && (
                <div className="col-span-full text-center py-12">
                  <Music className="h-16 w-16 mx-auto text-gray-400 dark:text-gray-600 mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">No recordings found</p>
                </div>
              )}
            </motion.div>
          )}

          {/* Table View */}
          {viewMode === 'table' && (
          <>
            {/* Batch Action Toolbar */}
            {selectedRecordingIds.size > 0 && (
              <motion.div
                className="mb-4 p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg flex items-center justify-between"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-purple-900 dark:text-purple-100">
                    {selectedRecordingIds.size} recording{selectedRecordingIds.size > 1 ? 's' : ''} selected
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedRecordingIds(new Set())}
                    className="text-purple-700 dark:text-purple-300 hover:text-purple-900 dark:hover:text-purple-100"
                  >
                    Clear Selection
                  </Button>
                </div>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleBatchDelete}
                  className="flex items-center gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete Selected
                </Button>
              </motion.div>
            )}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox
                    checked={selectedRecordingIds.size === filteredRecordings.length && filteredRecordings.length > 0}
                    indeterminate={selectedRecordingIds.size > 0 && selectedRecordingIds.size < filteredRecordings.length}
                    onChange={toggleSelectAll}
                  />
                </TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Venue</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Views</TableHead>
                <TableHead>Visibility</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRecordings.map((recording) => (
                <TableRow key={recording.id}>
                  <TableCell>
                    <Checkbox
                      checked={selectedRecordingIds.has(recording.id)}
                      onChange={() => toggleRecordingSelection(recording.id)}
                    />
                  </TableCell>
                  <TableCell className="font-medium">
                    <div>
                      <p>{recording.title}</p>
                      {recording.description && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {recording.description}
                        </p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{recording.venueName || 'Unknown'}</TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <p>{(() => {
                        const time = recording.startTime || recording['start_time'];
                        return time ? new Date(time).toLocaleDateString() : 'Unknown date';
                      })()}</p>
                      <p className="text-gray-500 dark:text-gray-400">
                        {(() => {
                          const time = recording.createdAt || recording['created_at'];
                          return time ? formatDistanceToNow(new Date(time), { addSuffix: true }) : '';
                        })()}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell>{formatDuration(recording.durationSeconds || recording.duration_seconds || 0)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Badge
                        variant={recording.status === 'ready' ? 'default' : 
                                recording.status === 'failed' ? 'destructive' :
                                recording.status === 'pending' ? 'outline' :
                                recording.status === 'queued' ? 'secondary' :
                                recording.status === 'recording' ? 'destructive' :
                                recording.status === 'uploading' ? 'secondary' : 'outline'}
                      >
                        {recording.status}
                      </Badge>
                      {(recording.status === 'recording' || recording.status === 'uploading') && (
                        <Loader2 className="h-3 w-3 animate-spin text-gray-500 dark:text-gray-400" />
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{recording.viewCount}</TableCell>
                  <TableCell>
                    {recording.isPublic ? (
                      <Badge variant="outline" className="text-green-600">
                        <Globe className="mr-1 h-3 w-3" />
                        Public
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-gray-600">
                        <Lock className="mr-1 h-3 w-3" />
                        Private
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {recording.status === 'ready' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                          onClick={() => handlePlay(recording)}
                          title="Play Recording"
                        >
                          <Music className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleEdit(recording)}
                        title="Edit Recording"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDelete(recording)}
                        title="Delete Recording"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </>
          )}

          {/* Empty state */}
          {filteredRecordings.length === 0 && (
            <motion.div
              className="text-center py-12"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <Music className="mx-auto h-16 w-16 mb-4 opacity-30 text-gray-400 dark:text-gray-600" />
              <p className="text-gray-600 dark:text-gray-400 text-lg font-medium">No recordings found</p>
              <p className="text-gray-500 dark:text-gray-500 text-sm mt-2">
                {filterVenue !== 'all' || searchQuery
                  ? 'Try adjusting your filters'
                  : 'Create your first recording to get started'}
              </p>
            </motion.div>
          )}
        </CardContent>
      </Card>

      {/* Dialogs */}
      {selectedRecording && (
        <>
          <ShareDialog
            recording={selectedRecording}
            open={shareDialogOpen}
            onClose={() => setShareDialogOpen(false)}
          />
          <EditRecordingDialog
            recording={selectedRecording}
            open={editDialogOpen}
            onClose={() => {
              setEditDialogOpen(false);
              fetchRecordings(); // Refresh after edit
            }}
          />
        </>
      )}
      
      <NewRecordingDialog
        open={newRecordingOpen}
        onClose={() => setNewRecordingOpen(false)}
        onRecordingStarted={() => {
          setNewRecordingOpen(false);
          fetchRecordings(); // Refresh the recordings list
        }}
      />

      {/* Floating widget for active recordings */}
      {showFloatingWidget && activeRecordings.length > 0 && (
        <FloatingRecordingWidget
          recordings={activeRecordings}
          isWebSocketConnected={true}
          onDismiss={() => setShowFloatingWidget(false)}
        />
      )}
    </div>
  );
};

export default RecordingDashboard;